import {
    Box,
    Checkbox,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
} from "@mui/material";
import React, {useEffect, useState, useMemo} from "react";
import PaginationActions from "./PaginationActions";
import {createSearchParams, useNavigate, useSearchParams, useLocation} from "react-router-dom";
import {ColumnDef, Filter, FilterDef, Order} from "./types";
import ExTableToolbar from "./ExTableToolbar";
import ExTableHead from "./ExTableHead";
import CustomTableCell from "./CustomTableCell";
import FilterEditor from "./FilterEditor";
import {ListQuery, ListRequest, BaseModelType} from "../../app/api/types";
import {useTranslation} from "react-i18next";
import {LabelDisplayedRowsArgs} from "@mui/material/TablePagination/TablePagination";
import QueryErrorAlert from "../QueryErrorAlert";
import {EMPTY_UI} from "../../variables";
import {rewriteFiltersForOneDayWorkaround, rewriteFiltersToEndOfDay} from "./WholeDay";
import {tableRowBackground} from "./Background";

export interface ExTableProps<T extends BaseModelType, U extends ListRequest> {
    /** Query hook exposed by RTK Query. */
    queryHook: ListQuery<T, U>;

    additionalRequestParams: U;

    /** Name of the key field of row items. */
    keyFieldName: keyof T;

    /** Descriptive name of the entity shown in the table. */
    title: string;

    /** Function that returns action button depending on the selected items. */
    renderActions?: (selected: T[]) => JSX.Element;

    /** Function that is called whenever the list of selected items changes. */
    onSelectedChange?: (selected: T[]) => void;

    /** Definition of table columns. TODO: should be delivered by server */
    columnDefs: ColumnDef<T>[];

    /** Definition of filters. TODO: should be delivered by server */
    filterDefs?: FilterDef<T>[];

    /** Define a polling interval to automatically trigger repolling of the table results */
    pollingInterval?: number;

    /** Whether the rows are selectable or not. Defaults to true */
    selectableRows?: boolean;

    /** Stay small */
    compact?: boolean;

    dataTest?: string;
}

function filterPreProcessing<T>(filterDefs: FilterDef<T>[] | undefined, filterJson: string): string {
    let filters: Filter[] = [];
    try {
        filters = JSON.parse(filterJson || "[]") as Filter[];
    } catch {
        return filterJson || "[]";
    }
    filters = rewriteFiltersForOneDayWorkaround(filterDefs, filters);
    filters = rewriteFiltersToEndOfDay(filterDefs, filters);
    return JSON.stringify(filters);
}

export default function ExTable<T extends BaseModelType, U extends ListRequest>({
    queryHook,
    additionalRequestParams,
    columnDefs,
    filterDefs,
    keyFieldName,
    title,
    renderActions,
    onSelectedChange,
    pollingInterval,
    selectableRows = true,
    compact = false,
    dataTest = "exTable",
}: ExTableProps<T, U>) {
    const {t} = useTranslation();
    const searchParams = useSearchParams()[0];
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedKeys, setSelectedKeys] = React.useState<any[]>([]);
    const [addFilter, setAddFilter] = useState<Filter | undefined>();
    const [triggerFocusLastFilter, setTriggerFocusLastFilter] = useState(0);
    const page = parseInt(searchParams.get("page") || "0");
    const pageSize = parseInt(searchParams.get("pageSize") || "100");
    const sort = searchParams.get("sort") || undefined;

    //Retrieve filter string from params, update it on change and parse it into filter array
    const filter = searchParams.get("filter");
    const [prevFilterString, setPrevFilterString] = useState(filter);
    const [filters, setFilters] = useState<Filter[]>(filter ? JSON.parse(filter) : []);
    if (prevFilterString !== filter) {
        setPrevFilterString(filter);
        setFilters(filter ? JSON.parse(filter) : []);
    }
    const {params: additionalParams, ...additionalRest} = additionalRequestParams;
    const {data, error} = queryHook(
        {
            ...additionalRest,
            params: {
                offset: page * pageSize,
                pageSize,
                ...(sort ? {sort} : {}),
                ...(filter ? {filter: filterPreProcessing(filterDefs, filter)} : {}),
                showTotal: true,
                ...additionalParams,
            },
        } as U,
        {pollingInterval: pollingInterval}
    );

    const currentSelection = useMemo(
        () => data?.payload?.filter((e: T) => selectedKeys.includes(e[keyFieldName])) || [],
        [JSON.stringify(data?.payload), JSON.stringify(selectedKeys)]
    );

    //Inform listener if selection changes
    useEffect(() => {
        if (onSelectedChange) {
            onSelectedChange(currentSelection);
        }
    }, [JSON.stringify(currentSelection)]);

    // Clear selection if data changes, e.g. after delete.
    useEffect(() => {
        setSelectedKeys(currentSelection.map((e: T) => e[keyFieldName]) || []);
    }, [JSON.stringify(data?.payload)]);

    if (!data) {
        if (error) {
            return <QueryErrorAlert error={error} />;
        }
        return <CircularProgress />;
    }

    const isPaginationSupported = !!data.pagination;

    const rows = data.payload;
    const rowCount = isPaginationSupported ? data.pagination?.total || 0 : rows.length;

    let order: Order = "asc";
    let orderBy = sort;
    if (sort && sort.startsWith("-")) {
        order = "desc";
        orderBy = sort.substring(1);
    }

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedKeys(rows.map((r) => r[keyFieldName]));
            return;
        }
        setSelectedKeys([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, entry: T) => {
        if (selectedKeys.includes(entry[keyFieldName])) {
            setSelectedKeys(selectedKeys.filter((k) => k !== entry[keyFieldName]));
        } else {
            setSelectedKeys(selectedKeys.concat(entry[keyFieldName]));
        }
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        navigate(
            {
                hash: location.hash,
                search: createSearchParams({
                    page: "0",
                    pageSize: `${parseInt(event.target.value, 10)}`,
                    filter: filter || "[]",
                }).toString(),
            },
            {replace: true}
        );
    };

    const handleColumnFilterButtonClick = (columnName: string) => {
        const filterDef = filterDefs?.find((f: FilterDef<T>) => f.fieldName === columnName);

        if (filterDef) {
            setAddFilter({f: columnName, o: filterDef.filterOperators[0], v: ""});
            setTriggerFocusLastFilter((i) => i + 1);
        }
    };

    const isSelected = (item: T) => selectedKeys.includes(item[keyFieldName]);

    return (
        <Box
            sx={{width: "100%", ...(compact ? {} : {height: "90vh"}), mb: 2, display: "flex", flexDirection: "column"}}
            data-test={dataTest}
        >
            <ExTableToolbar selection={currentSelection} title={title} renderActions={renderActions} />
            {filterDefs && filterDefs.length > 0 && (
                <FilterEditor
                    filterDefs={filterDefs}
                    columnDefs={columnDefs}
                    filters={filters}
                    addFilter={addFilter}
                    triggerFocusLast={triggerFocusLastFilter}
                />
            )}
            <TableContainer
                style={{
                    flexGrow: 10,
                    overflow: "auto",
                    ...(selectableRows ? {maxHeight: `${3 + 3.5 * rows.length}em`} : {}),
                }}
            >
                <Table sx={{}} aria-labelledby="tableTitle" size={"small"} stickyHeader>
                    <ExTableHead
                        numSelected={selectedKeys.length}
                        order={order}
                        orderBy={orderBy || undefined}
                        onSelectAllClick={selectableRows ? handleSelectAllClick : undefined}
                        rowCount={rows.length}
                        columnDefs={columnDefs}
                        filterDefs={filterDefs}
                        onFilterButtonClick={handleColumnFilterButtonClick}
                        selectableRows={selectableRows}
                    />
                    <TableBody>
                        {rows.map((row, index) => {
                            const isItemSelected = isSelected(row);
                            const labelId = `enhanced-table-checkbox-${index}`;

                            return (
                                <TableRow
                                    hover
                                    role="checkbox"
                                    aria-checked={isItemSelected}
                                    tabIndex={-1}
                                    key={`row_${index}`}
                                    selected={isItemSelected}
                                    sx={selectableRows ? {height: "3.5em"} : {}}
                                >
                                    {selectableRows && (
                                        <TableCell
                                            padding="none"
                                            sx={{
                                                background: tableRowBackground(index),
                                                width: 42,
                                                ...(columnDefs[0].sticky
                                                    ? {
                                                          position: "sticky",
                                                          zIndex: 3,
                                                          left: 0,
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            <Checkbox
                                                color="primary"
                                                checked={isItemSelected}
                                                inputProps={{
                                                    "aria-labelledby": labelId,
                                                }}
                                                onClick={(event) => handleClick(event, row)}
                                            />
                                        </TableCell>
                                    )}
                                    {columnDefs.map((columnDef, columnIndex) => {
                                        const link = columnDef.generateLink && columnDef.generateLink(row);
                                        const columnContent = columnDef.formatCellText
                                            ? columnDef.formatCellText({cellValue: row[columnDef.fieldName], row})
                                            : String(row[columnDef.fieldName] || EMPTY_UI);
                                        return (
                                            <CustomTableCell
                                                key={String(columnDef.fieldName)}
                                                align={columnDef.align}
                                                rowIndex={index}
                                                index={columnIndex}
                                                content={columnContent}
                                                link={link}
                                                sticky={columnDef.sticky}
                                                width={columnDef.width}
                                                minWidth={columnDef.minWidth}
                                                selectableRows={selectableRows}
                                            />
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {isPaginationSupported && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={rowCount}
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={() => {
                        return;
                    }}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={PaginationActions}
                    labelRowsPerPage={t("generic_rowsPerPage") + ":"}
                    labelDisplayedRows={(paginationInfo: LabelDisplayedRowsArgs) =>
                        t("generic_displayedRows", paginationInfo)
                    }
                    style={{overflow: "visible"}}
                />
            )}
        </Box>
    );
}
